import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient, getFullImageUrl } from '../lib/api';
import { PublicMenuResponse, Product, Menu, SubProduct } from '../types';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { Search, LogIn, Clock, Calendar, X } from 'lucide-react';
import { formatPreparationTime } from '../lib/format';

export function PublicMenuPage() {
    const { tenantUuid } = useParams<{ tenantUuid: string }>();
    const navigate = useNavigate();
    const [data, setData] = useState<PublicMenuResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeCategory, setActiveCategory] = useState<string>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

    useEffect(() => {
        if (!tenantUuid) {
            setError('ID de comercio no proporcionado');
            setLoading(false);
            return;
        }

        const loadMenu = async () => {
            try {
                const response = await apiClient.getPublicMenu(tenantUuid);
                if (response.success) {
                    setData(response);
                } else {
                    setError('No se pudo cargar el menú público.');
                }
            } catch (err: any) {
                console.error('Error loading public menu:', err);
                setError(err.message || 'Error al conectar con el servidor.');
            } finally {
                setLoading(false);
            }
        };

        loadMenu();
    }, [tenantUuid]);

    const handleBackToLogin = () => {
        const entryToken = sessionStorage.getItem('entry_token');
        if (entryToken) {
            navigate(`/${entryToken}`);
        } else {
            navigate('/login');
        }
    };

    const isMenuTimeValid = (menu: Menu): boolean => {
        if (!menu.schedule) return true;

        const dayMap = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
        const now = new Date();
        const todayKey = dayMap[now.getDay()];
        const todaySchedule = menu.schedule[todayKey] || menu.schedule[todayKey.normalize("NFD").replace(/[\u0300-\u036f]/g, "")];

        if (!todaySchedule) return false;

        let start = "";
        let end = "";

        if (todaySchedule && typeof todaySchedule === 'string') {
            const parts = todaySchedule.split('-');
            if (parts.length === 2) {
                start = parts[0];
                end = parts[1];
            }
        } else if (todaySchedule && typeof todaySchedule === 'object') {
            const ts = todaySchedule as any;
            if (ts.time) {
                start = ts.time.start;
                end = ts.time.end;
            } else if (ts.start && ts.end) {
                start = ts.start;
                end = ts.end;
            }
        }

        if (!start || !end) return true;

        const currentTime = now.getHours() * 60 + now.getMinutes();
        const [startH, startM] = start.split(':').map(Number);
        const [endH, endM] = end.split(':').map(Number);
        const startTime = startH * 60 + startM;
        let endTime = endH * 60 + endM;

        if (endTime < startTime) {
            return currentTime >= startTime || currentTime <= endTime;
        }

        return currentTime >= startTime && currentTime <= endTime;
    };

    const getMenuAvailabilityText = (menu: Menu) => {
        if (!menu.schedule) return "Disponible siempre";

        const dayLabels: Record<string, string> = {
            'lun': 'Lun', 'mar': 'Mar', 'mié': 'Mié', 'jue': 'Jue', 'vie': 'Vie', 'sáb': 'Sáb', 'dom': 'Dom'
        };

        const daysOrder = ['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'];
        const availableDays = Object.keys(menu.schedule).map(d => d.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, ""));
        const sortedDays = daysOrder.filter(day => availableDays.includes(day));

        if (sortedDays.length === 7) return "Disponible todos los días";
        if (sortedDays.length === 0) return "No disponible";

        const labels = sortedDays.map(d => dayLabels[d] || d);
        return `Disponible: ${labels.join(', ')}`;
    };

    if (loading) return <LoadingSpinner message="Preparando el menú..." />;
    if (error) return (
        <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gray-50 dark:bg-gray-900">
            <ErrorMessage message={error} />
            <button onClick={() => navigate(-1)} className="mt-4 text-sky-600 font-bold">Volver atrás</button>
        </div>
    );
    if (!data) return null;

    const { tenant, menus } = data;

    const filteredMenus = activeCategory === 'all'
        ? menus
        : menus.filter(m => m.id.toString() === activeCategory);

    const allProducts: { product: Product, menu: Menu }[] = [];
    filteredMenus.forEach(m => {
        m.products.forEach(p => {
            const isDuplicate = allProducts.some(ap => ap.product.uuid === p.uuid);
            const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                p.description.toLowerCase().includes(searchQuery.toLowerCase());
            if (!isDuplicate && matchesSearch) {
                allProducts.push({ product: p, menu: m });
            }
        });
    });

    return (
        <div className="min-h-screen pb-32" style={{ backgroundColor: 'var(--color-bg-page)' }}>
            {/* Premium Header/Banner */}
            <div className="relative h-64 overflow-hidden">
                {tenant.banner ? (
                    <img src={getFullImageUrl(tenant.banner)} alt={tenant.name} className="w-full h-full object-cover" />
                ) : (
                    <div className="w-full h-full bg-gradient-to-br from-sky-400 via-blue-500 to-purple-600" />
                )}
                <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />

                <button
                    onClick={handleBackToLogin}
                    className="absolute top-6 left-6 p-2.5 bg-black/20 hover:bg-black/40 backdrop-blur-md rounded-xl text-white border border-white/10 transition-all duration-300 z-50 group hover:scale-110"
                >
                    <LogIn className="w-5 h-5 rotate-180 group-hover:-translate-x-0.5 transition-transform" />
                </button>

                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 mt-8">
                    <div className="w-24 h-24 rounded-[2rem] bg-white p-1 shadow-2xl mb-4 overflow-hidden transform hover:scale-105 transition-transform duration-500 border-4 border-white/20">
                        <img src={getFullImageUrl(tenant.logotype)} alt={tenant.name} className="w-full h-full object-contain rounded-[1.75rem]" />
                    </div>
                    <h1 className="text-3xl font-bold text-white drop-shadow-lg mb-1">{tenant.name}</h1>
                    <p className="text-white/80 text-sm font-medium max-w-xs line-clamp-2 whitespace-pre-line">{tenant.description}</p>
                </div>
            </div>

            {/* Floating Search & Categories */}
            <div className="sticky top-0 z-30 transition-all duration-300">
                <div className="backdrop-blur-xl border-b border-gray-200 dark:border-gray-800 shadow-sm" style={{ backgroundColor: 'var(--color-bg-card)' }}>
                    <div className="px-6 py-4">
                        <div className="relative group">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-sky-500 transition-colors w-5 h-5 pointer-events-none" />
                            <input
                                type="text"
                                placeholder="¿Qué te gustaría probar hoy?"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-12 pr-4 py-3 bg-gray-100/50 dark:bg-gray-800/40 backdrop-blur-md border border-gray-200/20 dark:border-white/5 rounded-2xl text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-sky-500/50 transition-all outline-none"
                            />
                        </div>
                    </div>

                    <div className="px-6 pb-6 flex gap-3 overflow-x-auto no-scrollbar scroll-smooth">
                        {menus.map(menu => (
                            <button
                                key={menu.id}
                                onClick={() => setActiveCategory(menu.id.toString())}
                                className={`px-6 py-2 rounded-xl text-sm font-bold whitespace-nowrap transition-all duration-300 ${activeCategory === menu.id.toString() ? 'bg-sky-500 text-white shadow-lg' : 'bg-gray-100/80 dark:bg-gray-800/60 text-gray-600 dark:text-gray-400'}`}
                            >
                                {menu.name}
                            </button>
                        ))}
                        <button
                            onClick={() => setActiveCategory('all')}
                            className={`px-6 py-2 rounded-xl text-sm font-bold whitespace-nowrap transition-all duration-300 ${activeCategory === 'all' ? 'bg-sky-500 text-white shadow-lg' : 'bg-gray-100/80 dark:bg-gray-800/60 text-gray-600 dark:text-gray-400'}`}
                        >
                            Todos
                        </button>
                    </div>

                    <div className="px-6 pb-4 flex justify-center">
                        {activeCategory !== 'all' ? (
                            (() => {
                                const selectedMenu = menus.find(m => m.id.toString() === activeCategory);
                                if (selectedMenu?.schedule) {
                                    const isAvailable = isMenuTimeValid(selectedMenu);
                                    return (
                                        <div className={`flex items-center gap-2 px-4 py-2 rounded-2xl w-fit text-[11px] font-black uppercase tracking-wider backdrop-blur-md ${isAvailable ? 'bg-sky-500/10 text-sky-600 dark:text-sky-400' : 'bg-rose-500/10 text-rose-600 dark:text-rose-400'}`}>
                                            <Calendar className="w-3.5 h-3.5" />
                                            <span>{getMenuAvailabilityText(selectedMenu)}</span>
                                        </div>
                                    );
                                }
                                return null;
                            })()
                        ) : (
                            menus[0]?.schedule && (
                                <div className="flex items-center gap-2 px-4 py-2 bg-sky-500/10 text-sky-600 dark:text-sky-400 rounded-2xl w-fit text-[11px] font-black uppercase tracking-wider backdrop-blur-md">
                                    <Clock className="w-3.5 h-3.5" />
                                    <span>Explora nuestra carta</span>
                                </div>
                            )
                        )}
                    </div>
                </div>
            </div>

            {/* Product List */}
            <div className="px-6 py-8 space-y-8">
                {allProducts.length > 0 ? (
                    <div className="grid grid-cols-1 gap-6">
                        {allProducts.map(({ product, menu }) => {
                            const isAvailable = isMenuTimeValid(menu);
                            return (
                                <div
                                    key={product.uuid}
                                    onClick={() => setSelectedProduct(product)}
                                    className={`group bg-white dark:bg-gray-800/40 backdrop-blur-sm rounded-[2rem] overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100 dark:border-white/5 flex ${!isAvailable ? 'opacity-70' : ''} hover:-translate-y-1 cursor-pointer`}
                                >
                                    <div className="w-1/3 relative overflow-hidden">
                                        <img src={getFullImageUrl(product.image)} alt={product.name} className={`w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ${!isAvailable ? 'grayscale-[0.5]' : ''}`} />
                                    </div>
                                    <div className="w-2/3 p-5 flex flex-col justify-between">
                                        <div>
                                            <div className="flex justify-between items-start mb-2">
                                                <h3 className="font-bold text-gray-900 dark:text-white line-clamp-1 text-base">{product.name}</h3>
                                                <span className="text-sky-500 dark:text-sky-400 font-bold text-lg ml-2">${product.price ? product.price.toFixed(0) : '0'}</span>
                                            </div>
                                            <p className="text-gray-500 dark:text-gray-400 text-xs line-clamp-2 font-medium mb-3 whitespace-pre-line">{product.description}</p>
                                            <div className="space-y-2">
                                                {product.preparation_time && product.preparation_time !== '00:00:00' && (
                                                    <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                                                        <Clock className="w-3 h-3" />
                                                        <span>{formatPreparationTime(product.preparation_time)}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="py-20 text-center">
                        <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Search className="w-10 h-10 text-gray-300" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No encontramos resultados</h3>
                        <p className="text-gray-500 font-medium">Prueba buscando con otras palabras.</p>
                    </div>
                )}
            </div>

            {/* Product Detail Modal */}
            {selectedProduct && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-300">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={() => setSelectedProduct(null)} />
                    <div className="relative bg-white dark:bg-[#161b2c] w-full max-w-lg rounded-[2.5rem] overflow-hidden shadow-2xl border border-white/10 animate-in zoom-in-95 duration-300">
                        <div className="relative h-64 sm:h-80 w-full">
                            <img src={getFullImageUrl(selectedProduct.image)} alt={selectedProduct.name} className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#161b2c] via-transparent opacity-60" />
                            <button onClick={() => setSelectedProduct(null)} className="absolute top-6 right-6 p-3 bg-black/20 hover:bg-black/40 backdrop-blur-md rounded-2xl text-white border border-white/10 transition-all active:scale-95">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-8 sm:p-10 space-y-6">
                            <div className="flex justify-between items-start gap-4">
                                <h1 className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-white leading-tight">{selectedProduct.name}</h1>
                                <span className="text-2xl font-black text-sky-500 dark:text-sky-400 shrink-0">${selectedProduct.price ? selectedProduct.price.toFixed(0) : '0'}</span>
                            </div>
                            <div className="flex flex-wrap gap-3">
                                {selectedProduct.preparation_time && selectedProduct.preparation_time !== '00:00:00' && (
                                    <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-800/50 rounded-xl text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                                        <Clock className="w-3.5 h-3.5" />
                                        <span>{formatPreparationTime(selectedProduct.preparation_time)}</span>
                                    </div>
                                )}
                            </div>
                            <div className="space-y-4 max-h-[150px] sm:max-h-[250px] overflow-y-auto custom-scrollbar pr-2 text-gray-600 dark:text-gray-300 text-sm sm:text-base leading-relaxed font-medium whitespace-pre-line pb-4">
                                {selectedProduct.description}
                                
                                {/* Side Dish Selector for Public Menu (Display only) */}
                                {selectedProduct.sub_products && selectedProduct.sub_products.length > 0 && (
                                    <div className="mt-8 space-y-4 border-t border-gray-100 dark:border-white/5 pt-6">
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-[0.1em]">Guarniciones disponibles</h3>
                                            <span className="px-3 py-1 bg-sky-500/10 text-sky-600 dark:text-sky-400 text-[10px] font-black rounded-xl uppercase tracking-wider">Opcional</span>
                                        </div>
                                        <div className="grid grid-cols-2 xs:grid-cols-3 gap-3">
                                            {selectedProduct.sub_products.map((sub: SubProduct) => (
                                                <div
                                                    key={sub.sub_product_uuid}
                                                    className="group/sub relative bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-white/5 rounded-2xl overflow-hidden p-2 transition-all hover:border-sky-500/50"
                                                >
                                                    <div className="aspect-square w-full rounded-xl overflow-hidden mb-2 bg-gray-200 dark:bg-gray-700">
                                                        <img 
                                                            src={getFullImageUrl(sub.sub_product_image || '')} 
                                                            alt={sub.sub_product_name} 
                                                            className="w-full h-full object-cover group-hover/sub:scale-110 transition-transform duration-500"
                                                        />
                                                    </div>
                                                    <div className="px-1 text-center">
                                                        <p className="text-[10px] font-bold text-gray-800 dark:text-gray-200 leading-tight mb-1">{sub.sub_product_name}</p>
                                                        <p className="text-[9px] font-black text-sky-500">+${sub.additional_price.toFixed(0)}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
